import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "step" }
const _hoisted_2 = { class: "step-header" }
const _hoisted_3 = { class: "step-content" }
const _hoisted_4 = { class: "form-field" }
const _hoisted_5 = { class: "service-list" }
const _hoisted_6 = { class: "service-left" }
const _hoisted_7 = { class: "service-title" }
const _hoisted_8 = { class: "service-subtitle" }
const _hoisted_9 = { class: "selected-hint" }

import { computed, onMounted, ref, watch } from "vue";
import AppSearchField from "@/shared/components/ui/AppSearchField.vue";
import { useModalStore } from "@/shared/stores/modal";
import { useAppStore } from "@/shared/stores/app";
import { getActiveContext } from "@/modules/auth/services/auth.storage";
import AddServiceModal from "@/modules/vendor/components/profile-setup/AddServiceModal.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

type ServiceOption = {
  service_name: string;
  subtitle: string;
  price?: number;
};

type ServiceTemplate = { service_name?: string; subtitle?: string };
type ServiceGroup = {
  id?: number | string;
  name?: string;
  label?: string;
  routeName?: string;
  route_name?: string;
  service_templates?: ServiceTemplate[];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceTemplateStep',
  props: {
    current_step: {},
    errors: {},
    profileInfo: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const modal = useModalStore();
const appStore = useAppStore();

function normalizeText(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function extractServiceGroupId(profileInfo: any): number | null {
  const direct = Number(profileInfo?.service_group_id ?? profileInfo?.serviceGroupId ?? NaN);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const nested = Number(profileInfo?.service_group?.id ?? NaN);
  if (Number.isFinite(nested) && nested > 0) return nested;

  return null;
}

function extractServiceGroupName(profileInfo: any): string {
  const n1 = normalizeText(profileInfo?.service_group?.name);
  if (n1) return n1;
  const n2 = normalizeText(profileInfo?.service_group_name);
  if (n2) return n2;
  const n3 = normalizeText(profileInfo?.service_group?.label);
  if (n3) return n3;
  return "";
}

function normalizeSlug(value: unknown) {
  return normalizeText(value).replace(/_/g, "-").replace(/\s+/g, "-");
}

const activeVendorType = computed(() => {
  const ctx: any = getActiveContext() || {};
  const vendorTypeRaw = ctx?.vendor?.type ?? ctx?.vendor_type ?? "";
  return normalizeSlug(vendorTypeRaw);
});

function matchesVendorType(group: ServiceGroup, vendorType: string) {
  if (!vendorType) return true;
  const routeName = normalizeSlug(group?.routeName ?? group?.route_name ?? "");
  if (routeName && routeName.includes(vendorType)) return true;
  const name = normalizeSlug(group?.name ?? "");
  if (name && name === vendorType) return true;
  const label = normalizeSlug(group?.label ?? "");
  if (label && label === vendorType) return true;
  return false;
}

const selectedServiceGroup = computed<ServiceGroup | null>(() => {
  const groups = (Array.isArray(appStore.serviceGroups) ? appStore.serviceGroups : []) as ServiceGroup[];
  if (!groups.length) return null;

  // Filter groups by active vendor type (auth_active_context.vendor.type) when present.
  const vendorType = activeVendorType.value;
  const scopedGroups = vendorType ? groups.filter((g) => matchesVendorType(g, vendorType)) : groups;
  if (!scopedGroups.length) return null;

  const groupId = extractServiceGroupId(props.profileInfo);
  if (groupId != null) {
    const byId = scopedGroups.find((g) => Number(g?.id ?? NaN) === groupId) || null;
    if (byId) return byId;
  }

  const groupName = extractServiceGroupName(props.profileInfo);
  if (groupName) {
    const byName =
      scopedGroups.find((g) => normalizeText(g?.name) === groupName) ||
      scopedGroups.find((g) => normalizeText(g?.label) === groupName) ||
      null;
    if (byName) return byName;
  }

  const nonEmpty = scopedGroups.filter((g) => Array.isArray(g?.service_templates) && g.service_templates!.length > 0);
  if (nonEmpty.length === 1) return nonEmpty[0] as ServiceGroup;

  return null;
});

function getAllServiceTemplates(groups: ServiceGroup[] = []): ServiceOption[] {
  const seen = new Set<string>();
  const out: ServiceOption[] = [];
  const vendorType = activeVendorType.value;
  groups.forEach((g) => {
    if (vendorType && !matchesVendorType(g, vendorType)) return;
    const templates = Array.isArray(g?.service_templates) ? g.service_templates : [];
    templates.forEach((t) => {
      const service_name = String(t?.service_name ?? "").trim();
      if (!service_name) return;
      const key = normalizeText(service_name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({
        service_name,
        subtitle: String(t?.subtitle ?? "").trim(),
      });
    });
  });
  return out;
}

const baseServiceOptions = computed<ServiceOption[]>(() => {
  const templates = (selectedServiceGroup.value?.service_templates ?? []) as ServiceTemplate[];
  if (templates.length) {
    return templates
      .map((t) => {
        const service_name = String(t?.service_name ?? "").trim();
        if (!service_name) return null;
        return {
          service_name,
          subtitle: String(t?.subtitle ?? "").trim(),
        } as ServiceOption;
      })
      .filter(Boolean) as ServiceOption[];
  }

  // If nothing is selected/created yet, show all available templates across groups.
  const apiList = Array.isArray(props.profileInfo?.service_list) ? props.profileInfo.service_list : [];
  const hasExistingSelections = selectedServices.value.length > 0;
  const hasAnyOptions = serviceOptions.value.length > 0;
  if (hasExistingSelections || apiList.length > 0 || hasAnyOptions) return [];

  const groups = (Array.isArray(appStore.serviceGroups) ? appStore.serviceGroups : []) as ServiceGroup[];
  return getAllServiceTemplates(groups);
});

const serviceOptions = ref<ServiceOption[]>([]);
const selectedServices = ref<string[]>([]);
const search = ref("");
const serverError = ref("");

onMounted(async () => {
  if (appStore.status !== "ready") {
    await appStore.loadAppInit();
  }
});

watch(
  baseServiceOptions,
  (base) => {
    if (!base.length) return;

    // Treat app-init templates as defaults, but don't drop any existing entries (added via API or modal).
    if (!serviceOptions.value.length) {
      serviceOptions.value = base;
      return;
    }

    const existingMap = new Map(serviceOptions.value.map((s) => [s.service_name, s]));
    const merged = [...serviceOptions.value];
    base.forEach((svc) => {
      if (!existingMap.has(svc.service_name)) merged.push(svc);
    });
    serviceOptions.value = merged;
  },
  { immediate: true }
);

const filteredServices = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return serviceOptions.value;
  return serviceOptions.value.filter((s) => {
    return (
      s.service_name.toLowerCase().includes(q) || s.subtitle.toLowerCase().includes(q)
    );
  });
});

watch(
  () => props.profileInfo,
  (info) => {
    const apiList = Array.isArray(info?.service_list) ? info.service_list : [];
    if (!apiList.length) return;

    const normalized: ServiceOption[] = apiList
      .map((s: any) => {
        const name = String(s?.service_name || "").trim();
        if (!name) return null;
        const subtitle = String(s?.subtitle || "").trim();
        const price = Number(s?.price ?? 0);
        return {
          service_name: name,
          subtitle,
          price: Number.isFinite(price) ? price : 0,
        } as ServiceOption;
      })
      .filter(Boolean) as ServiceOption[];

    if (!normalized.length) return;

    // Merge API options into existing options (do not lose local defaults).
    const existingMap = new Map(serviceOptions.value.map((s) => [s.service_name, s]));
    normalized.forEach((svc) => {
      if (!existingMap.has(svc.service_name)) {
        serviceOptions.value = [svc, ...serviceOptions.value];
      }
    });

    // Prefill selected services only when user hasn't selected anything yet.
    if (!selectedServices.value.length) {
      selectedServices.value = normalized.map((s) => s.service_name);
    }
  },
  { immediate: true }
);

// Map props.errors to a friendly serverError message to display under the search field
watch(
  () => props.errors,
  (errs: any) => {
    serverError.value = "";
    if (!errs) return;
    // Prefer errors.services or any key containing 'service'
    if (errs.services) {
      serverError.value = Array.isArray(errs.services)
        ? errs.services.join(" ")
        : String(errs.services);
      return;
    }
    const serviceKey = Object.keys(errs).find((k) => /service/i.test(k));
    if (serviceKey) {
      const v = errs[serviceKey];
      serverError.value = Array.isArray(v) ? v.join(" ") : String(v);
      return;
    }
    // Fallback: join all error messages into one string
    const msgs: string[] = [];
    Object.values(errs).forEach((val: any) => {
      if (Array.isArray(val)) msgs.push(val.join(" "));
      else if (typeof val === "string") msgs.push(val);
      else if (typeof val === "object") msgs.push(JSON.stringify(val));
    });
    serverError.value = msgs.join(" ");
  },
  { immediate: true }
);

function getValues() {
  // Only include services that the user has selected. Map to the shape expected by the server.
  const servicesToSend = selectedServices.value.map((name) => {
    const svc = serviceOptions.value.find((s) => s.service_name === name);
    return {
      service_name: svc?.service_name || name,
      subtitle: svc?.subtitle || "",
      price: (svc as any)?.price || 0,
    };
  });

  return {
    services: servicesToSend,
    search: search.value,
    selectedServiceIds: selectedServices.value,
  };
}

__expose({ getValues });

function openAddServiceModal() {
  const existingIds = serviceOptions.value.map((s) => s.service_name);
  modal.open(
    AddServiceModal,
    {
      existingIds,
      onAdd: (service: ServiceOption) => {
        serviceOptions.value = [service, ...serviceOptions.value];
      },
    },
    { fullscreen: true, title: "Add Service", showHeader: false }
  );
}

function toggle(serviceName: string, isSelected: boolean) {
  if (isSelected) {
    selectedServices.value.push(serviceName);
  } else {
    const index = selectedServices.value.indexOf(serviceName);
    if (index > -1) {
      selectedServices.value.splice(index, 1);
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_checkbox = _resolveComponent("v-checkbox")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(AppButtonFlat, {
            color: "primary",
            variant: "outlined",
            "prepend-icon": "mdi-plus",
            onClick: openAddServiceModal
          }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
              _createTextVNode(" Add service ", -1)
            ]))]),
            _: 1
          })
        ])
      ])
    ]),
    _cache[2] || (_cache[2] = _createElementVNode("h2", { class: "title text-success" }, "Select the services you offer. You can customize details later.", -1)),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredServices.value, (service, index) => {
        return (_openBlock(), _createElementBlock("label", {
          key: index,
          class: "service-item"
        }, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, _toDisplayString(service.service_name), 1),
            _createElementVNode("div", _hoisted_8, _toDisplayString(service.subtitle), 1)
          ]),
          _createVNode(_component_v_checkbox, {
            class: "service-check",
            density: "compact",
            "hide-details": "",
            "model-value": selectedServices.value.includes(service.service_name),
            "onUpdate:modelValue": ($event: any) => (toggle(service.service_name, Boolean($event)))
          }, null, 8, ["model-value", "onUpdate:modelValue"])
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_9, [
      _cache[1] || (_cache[1] = _createTextVNode(" Selected: ", -1)),
      _createElementVNode("strong", null, _toDisplayString(selectedServices.value.length), 1)
    ])
  ]))
}
}

})